import type { Question } from "@/commonTypes/question";
import type { QuestionOverride, ReplaceableProperty } from "@/api/types";

/**
 * Moves a question to a new position in the questions list.
 *
 * @param questions - The list of questions to reorder
 * @param questionToMoveId - The ID of the question to move
 * @param afterQuestionId - The ID of the question to place the moved question after.
 *                          If undefined, the question is moved to the beginning.
 * @returns A new list with the question repositioned, or the original list if
 *          the questionToMoveId is not found or afterQuestionId is specified but not found.
 */
export function applyMoveOverride(
  questions: Question[],
  questionToMoveId: string,
  afterQuestionId?: string,
): Question[] {
  // Can't move a question after itself - return unchanged
  if (questionToMoveId === afterQuestionId) {
    return questions;
  }

  // Find the index of the question to move
  const fromIndex = questions.findIndex(
    (question) => question.questionId === questionToMoveId,
  );

  // Return unchanged if the question to move was not found
  if (fromIndex === -1) {
    return questions;
  }

  let toIndex: number;

  // If no afterQuestionId was passed, move the question to the start
  if (afterQuestionId == undefined) {
    toIndex = 0;
  }
  // Find the index of the question to move after
  else {
    const afterIndex = questions.findIndex(
      (question) => question.questionId === afterQuestionId,
    );

    // Return unchanged if the question to move after was not found
    if (afterIndex === -1) {
      return questions;
    }

    toIndex = afterIndex + 1;
  }

  // No move needed if question is already at the target position
  if (fromIndex === toIndex) {
    return questions;
  }

  // Remove the question from its current position
  const questionToMove = questions[fromIndex];
  const remainingQuestions = [
    ...questions.slice(0, fromIndex),
    ...questions.slice(fromIndex + 1),
  ];

  // Adjust insertion index if we removed an element before it
  const adjustedToIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;

  // Insert the question at the new position
  return [
    ...remainingQuestions.slice(0, adjustedToIndex),
    questionToMove,
    ...remainingQuestions.slice(adjustedToIndex),
  ];
}

/**
 * Replaces a property on a specific question.
 *
 * @param questions - The list of questions to modify
 * @param questionId - The ID of the question to modify
 * @param propertyName - The property name to replace
 * @param newValue - The new value for the property
 * @returns A new list with the property replaced, or the original list if
 *          the questionId is not found or the property doesn't exist.
 */
function applyQuestionPropertyReplacement(
  questions: Question[],
  questionId: string,
  propertyName: ReplaceableProperty,
  newValue: string,
): Question[] {
  // Find the question to do the replacement on
  const questionIndex = questions.findIndex(
    (question) => question.questionId === questionId,
  );

  // Return unchanged if the question wasn't found
  if (questionIndex === -1) {
    return questions;
  }

  const question = questions[questionIndex];

  // Return unchanged if property to change doesn't exist on the question
  if (!(propertyName in question)) {
    return questions;
  }

  // Create a new question with the replaced property
  const updatedQuestion = {
    ...question,
    [propertyName]: newValue,
  };

  // Return a new list with the updated question
  return [
    ...questions.slice(0, questionIndex),
    updatedQuestion,
    ...questions.slice(questionIndex + 1),
  ];
}

/**
 * Replaces a property on a specific answer within a question.
 *
 * @param questions - The list of questions to modify
 * @param questionId - The ID of the question containing the answer
 * @param answerId - The ID of the answer to modify
 * @param propertyName - The property name to replace
 * @param newValue - The new value for the property
 * @returns A new list with the property replaced, or the original list if
 *          the questionId/answerId is not found or the property doesn't exist.
 */
function applyAnswerPropertyReplacement(
  questions: Question[],
  questionId: string,
  answerId: string,
  propertyName: ReplaceableProperty,
  newValue: string,
): Question[] {
  // Find the question to do the replacement on
  const questionIndex = questions.findIndex(
    (question) => question.questionId === questionId,
  );

  // Return unchanged if the question wasn't found
  if (questionIndex === -1) {
    return questions;
  }

  const question = questions[questionIndex];

  // Check if question has answers array
  if (!("answers" in question) || !Array.isArray(question.answers)) {
    return questions;
  }

  // Find the answer to do the replacement on
  const answerIndex = question.answers.findIndex(
    (answer: { id?: string }) => answer.id === answerId,
  );

  // Return unchanged if answer not found
  if (answerIndex === -1) {
    return questions;
  }

  const answer = question.answers[answerIndex];

  // Return unchanged if property doesn't exist on the answer
  if (!(propertyName in answer)) {
    return questions;
  }

  // Create updated answer
  const updatedAnswer = {
    ...answer,
    [propertyName]: newValue,
  };

  // Create updated answers list
  const updatedAnswers = [
    ...question.answers.slice(0, answerIndex),
    updatedAnswer,
    ...question.answers.slice(answerIndex + 1),
  ];

  // Create updated question with new answers
  const updatedQuestion = {
    ...question,
    answers: updatedAnswers,
  };

  // Return a new questions list with the updated question
  return [
    ...questions.slice(0, questionIndex),
    updatedQuestion,
    ...questions.slice(questionIndex + 1),
  ] as Question[];
}

/**
 * Applies a list of question overrides to the questions list.
 * Currently supports the "move" and "replace" actions.
 *
 * @param questions - The list of questions to modify
 * @param overrides - The list of override actions to apply
 * @returns A new list with all overrides applied sequentially
 */
export function applyQuestionOverrides(
  questions: Question[],
  overrides: QuestionOverride[],
): Question[] {
  return overrides.reduce((currentQuestions, override) => {
    switch (override.action) {
      case "move":
        return applyMoveOverride(
          currentQuestions,
          override.questionId,
          override.after,
        );
      case "replace":
        if (override.answerId != null) {
          return applyAnswerPropertyReplacement(
            currentQuestions,
            override.questionId,
            override.answerId,
            override.propertyName,
            override.newValue,
          );
        }
        return applyQuestionPropertyReplacement(
          currentQuestions,
          override.questionId,
          override.propertyName,
          override.newValue,
        );
      default: {
        const unknownOverride = override as { action: string };
        console.warn(`Unknown override action: ${unknownOverride.action}`);
        return currentQuestions;
      }
    }
  }, questions);
}
